import { computed, makeObservable } from 'mobx';

import { IRootStore } from 'shared/entities/store/rootStore';
import { AIEducationParamsModel } from 'shared/models/smartbotAI/AIEducationParamsModel';
import { AIBotModelBase } from 'shared/models/smartbotAI/AIBotModelBase';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { ISmartbotAIBotsStore } from 'shared/entities/store/smartbotAIBotsStore';
import NewListModel from 'shared/models/NewListModel';
import { apiUrls, urls, SmartbotAITabs } from 'shared/entities/domain';
import { AIBotServer } from 'shared/entities/smartbotAI';
import { FieldModel } from 'shared/models/form';
import { ComponentLoadingStore } from 'stores/componentLoadingStore';
import { AnalyticsEvent } from 'shared/entities/analytics';
import { AIBotModel } from 'shared/models/smartbotAI/AIBotModel';

export class SmartbotAIBotsStore
  extends ComponentLoadingStore
  implements ISmartbotAIBotsStore
{
  readonly rootStore: IRootStore;
  readonly bots: NewListModel<AIBotModelBase> =
    new NewListModel<AIBotModelBase>();
  readonly loadingStage: LoadingStageModel = new LoadingStageModel();
  readonly removingStage: LoadingStageModel = new LoadingStageModel();
  readonly searchText: FieldModel<string> = new FieldModel<string>('');
  readonly creatingStage: LoadingStageModel = new LoadingStageModel();

  constructor(rootStore: IRootStore) {
    super();
    this.rootStore = rootStore;

    makeObservable(this, {
      filteredBots: computed
    });
  }

  get loaded(): boolean {
    return this.loadingStage.successfullyLoaded;
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized;
  }

  /*
   * проверка на вхождение searchText
   */
  get filteredBots(): AIBotModelBase[] {
    return this.bots.items.filter((bot) =>
      bot.name
        .split(' ')
        .some((word) =>
          word.toLowerCase().includes(this.searchText.value.toLowerCase())
        )
    );
  }

  async load(): Promise<BaseResponse> {
    if (this.loadingStage.isLoading) {
      return {
        isError: true
      };
    }
    this.loadingStage.loading();

    const response = await this.rootStore.networkStore.api<{
      ai_bots: AIBotServer[];
    }>(apiUrls.AI_BOTS_LIST);

    if (response.isError) {
      this.loadingStage.error();
      return {
        isError: true
      };
    }

    this.bots.fillByRawData({
      raw: response.data.ai_bots,
      normalizer: (rawItem) => {
        return {
          entity: AIBotModelBase.fromJson(rawItem, this.rootStore),
          key: rawItem._id
        };
      }
    });

    this.loadingStage.success();
    this.bots.isInitialLoadModel.changeValue(false);
    return {
      isError: false
    };
  }

  removeBot = async (id: string): Promise<BaseResponse> => {
    if (this.removingStage.isLoading) {
      return {
        isError: true
      };
    }

    this.removingStage.loading();
    const response = await this.rootStore.networkStore.api(
      apiUrls.AI_BOTS_DELETE,
      {
        method: 'POST',
        data: {
          _id: id
        }
      }
    );

    if (response.isError) {
      this.removingStage.error();
      return {
        isError: true
      };
    }

    this.bots.removeEntity(id);

    this.removingStage.success();
    return {
      isError: false
    };
  };

  createBot = async (
    {
      openingTab,
      projectId
    }: { openingTab: SmartbotAITabs | null; projectId?: string } = {
      openingTab: SmartbotAITabs.INSTRUCTION
    }
  ): Promise<BaseResponse<AIBotModel>> => {
    const actualProjectId = projectId ?? this.rootStore.projectId;

    if (this.creatingStage.isLoading || !actualProjectId) {
      return {
        isError: true
      };
    }

    this.creatingStage.loading();

    const paramsModel = AIEducationParamsModel.fromDefaultParams(
      this.rootStore
    );
    const params = paramsModel.toJson();
    const response = await this.rootStore.networkStore.api<AIBotServer>(
      apiUrls.AI_BOTS_CREATE,
      {
        method: 'POST',
        data: {
          ...params,
          projectId: actualProjectId
        }
      }
    );

    if (response.isError) {
      this.creatingStage.error();

      this.rootStore.routerStore.push(
        urls.PROJECT.tabs.SMARTBOT_AI.LIST.create({
          projectId: actualProjectId
        })
      );

      return {
        isError: true
      };
    }

    this.rootStore.analyticsStore.sendEvent(AnalyticsEvent.smartbotAIBotCreate);

    const newBot = AIBotModel.fromJson(response.data, this.rootStore);

    this.bots.addEntity({ entity: newBot, key: newBot.id.value, start: true });
    this.creatingStage.success();

    if (openingTab) {
      this.rootStore.routerStore.push(
        urls.PROJECT.tabs.SMARTBOT_AI.BOT.create({
          projectId: actualProjectId,
          tab: openingTab,
          botId: newBot.id.value
        })
      );
    }

    return {
      isError: false,
      data: newBot
    };
  };

  reset(): void {
    this.bots.reset();
    this.loadingStage.reset();
    this.removingStage.reset();
    this.searchText.changeValue('');
  }
}
